import type {
  EChartsOption,
  LineSeriesOption,
  ScatterSeriesOption,
  XAXisComponentOption,
  YAXisComponentOption
} from 'echarts';
import { i18n } from 'i18n';
import { isNumber } from 'utils';
import { buildHglFilename, HglPoint } from './hglCsvExport';
import calculateNiceInterval from './niceInterval';

export interface HglImageSeries {
  name: string;
  data: HglPoint[];
  color?: string;
}

export interface HglImageMarkerGroup {
  name: string;
  color: string;
  data: HglPoint[];
  lines: number[];
}

export interface HglImageSummary {
  stopeName?: string;
  pumpPressure?: number;
  yieldStressPa?: number;
  massConcentrationPercent?: number;
  flowRate?: number;
  flowRateAbbr: string;
}

export interface HglImageContext {
  series: HglImageSeries[];
  markerGroups: HglImageMarkerGroup[];
  summary: HglImageSummary;
  distanceAbbr: string;
  pressureSeriesName: string;
  pressureAbbr: string;
  dashedSeriesNames: string[];
  formatNumber: (value: number, decimals: number) => string;
}

export interface DistanceWindow {
  min: number;
  max: number;
}

interface AxisScale {
  min: number;
  max: number;
  interval: number;
}

interface LineLook {
  label: string;
  color: string;
  width: number;
  dashed: boolean;
  onPressureAxis: boolean;
}

const WIDTH = 1000;
const HEIGHT = 650;
const PIXEL_RATIO = 2;
const INK = '#000000';
const PAPER = '#FFFFFF';
const EXCEL_BLUE = '#0070C0';
const EXCEL_RED = '#FF0000';
const FONT_FAMILY = 'Calibri, Carlito, Arial, sans-serif';
const TITLE_SEPARATOR = '    ';
const MINOR_TICKS_PER_INTERVAL = 5;
const ELEVATION_PADDING = 0.1;
const X_AXIS_NAME_GAP = 34;
const X_AXIS_NAME_HEIGHT = 16;
const LEGEND_BOTTOM = 12;
const LEGEND_GAP_BELOW_AXIS_NAME = 14;
const LEGEND_ITEM_GAP = 20;
const LEGEND_ICON_WIDTH = 36;
const LEGEND_TEXT_OFFSET = 5;
const LEGEND_ROW_HEIGHT = 20;
const LEGEND_CHAR_WIDTH = 6.5;
const LEGEND_MAX_WIDTH = WIDTH - 40;
const LINE_ONLY_LEGEND_ICON = { opacity: 0 };

const isPlotted = (point: HglPoint) => isNumber(point?.x) && isNumber(point?.y);

const lookFor = (name: string, color: string | undefined, context: HglImageContext): LineLook => {
  const {
    distanceAbbr, pressureSeriesName, pressureAbbr, dashedSeriesNames,
  } = context;
  const inDistance = (label: string) => `${label} (${distanceAbbr})`;
  const base: LineLook = {
    label: name,
    color: !color || color.toUpperCase() === PAPER ? INK : color,
    width: 2,
    dashed: dashedSeriesNames.includes(name),
    onPressureAxis: false,
  };

  if (name === i18n._('Pipe Profile')) return { ...base, label: inDistance(name), color: INK, width: 1 };
  if (name === i18n._('Pipe Rating')) return { ...base, label: inDistance(i18n._('Pressure Rating')), color: EXCEL_RED };
  if (name === i18n._('HGL')) return { ...base, label: inDistance(name), color: EXCEL_BLUE };
  if (name === pressureSeriesName) {
    return {
      ...base, label: `${name} (${pressureAbbr})`, color: EXCEL_BLUE, width: 1, dashed: true, onPressureAxis: true,
    };
  }
  return base;
};

const scaleOf = (values: number[], targetTicks: number, axis: 'x' | 'y', padding = 0): AxisScale | undefined => {
  if (values.length === 0) return undefined;
  const low = Math.min(...values);
  const high = Math.max(...values);
  const pad = (high - low) * padding;
  const { niceMin, niceMax, interval } = calculateNiceInterval(low - pad, high + pad, targetTicks, axis);
  return { min: niceMin, max: niceMax, interval };
};

const decimalsFor = ({ interval }: AxisScale) => (interval >= 1 ? 0 : Math.ceil(-Math.log10(interval)));

const estimateLegendRows = (labels: string[]): number => labels.reduce(({ rows, used }, label) => {
  const width = LEGEND_ICON_WIDTH + LEGEND_TEXT_OFFSET + label.length * LEGEND_CHAR_WIDTH;
  const needed = used === 0 ? width : used + LEGEND_ITEM_GAP + width;
  return needed > LEGEND_MAX_WIDTH && used > 0 ? { rows: rows + 1, used: width } : { rows, used: needed };
}, { rows: 1, used: 0 }).rows;

const buildHglImageTitle = ({ summary, pressureAbbr, formatNumber }: HglImageContext): { text: string; subtext: string } => {
  const {
    stopeName, pumpPressure, yieldStressPa, massConcentrationPercent, flowRate, flowRateAbbr,
  } = summary;
  const parts = [
    isNumber(pumpPressure) ? `${i18n._('Plant Pump')} = ${formatNumber(pumpPressure, 0)} ${pressureAbbr}` : '',
    isNumber(yieldStressPa) ? `${i18n._('Yield Stress')} = ${formatNumber(yieldStressPa, 0)} Pa` : '',
    isNumber(massConcentrationPercent) ? `${i18n._('Mass Conc.')} = ${formatNumber(massConcentrationPercent, 1)}%m` : '',
    isNumber(flowRate) ? `${i18n._('Flow Rate')} = ${formatNumber(flowRate, 0)} ${flowRateAbbr}` : '',
  ];
  return {
    text: stopeName ? `${i18n._('Fill Point')}: ${stopeName}` : '',
    subtext: parts.filter(Boolean).join(TITLE_SEPARATOR),
  };
};

const valueAxis = (name: string, scale: AxisScale | undefined, formatNumber: HglImageContext['formatNumber']) => ({
  type: 'value' as const,
  name,
  nameLocation: 'middle' as const,
  nameTextStyle: { color: INK, fontSize: 14 },
  ...(scale ? { min: scale.min, max: scale.max, interval: scale.interval } : {}),
  axisLine: { show: true, onZero: false, lineStyle: { color: INK } },
  axisTick: { show: true, inside: false, length: 6, lineStyle: { color: INK } },
  minorTick: {
    show: true, splitNumber: MINOR_TICKS_PER_INTERVAL, length: 3, lineStyle: { color: INK },
  },
  splitLine: { show: false },
  minorSplitLine: { show: false },
  axisLabel: {
    color: INK,
    fontSize: 13,
    formatter: (value: number) => formatNumber(value, scale ? decimalsFor(scale) : 0),
  },
});

const buildHglImageOption = (context: HglImageContext, view?: DistanceWindow): EChartsOption => {
  const {
    series, markerGroups, distanceAbbr, pressureAbbr, formatNumber,
  } = context;

  const lines = series
    .map(({ name, data, color }) => ({ look: lookFor(name, color, context), points: data.filter(isPlotted) }))
    .filter(({ points }) => points.length > 0);
  const markers = markerGroups
    .map((group) => ({ ...group, data: group.data.filter(isPlotted) }))
    .filter(({ data, lines: positions }) => data.length > 0 || positions.length > 0);

  const distance = scaleOf(view ? [view.min, view.max] : lines.flatMap(({ points }) => points.map(({ x }) => x)), 10, 'x');
  const inView = ({ x }: HglPoint) => !distance || (x >= distance.min && x <= distance.max);
  const heightsOf = (points: HglPoint[]) => {
    const visible = points.filter(inView);
    return (visible.length > 0 ? visible : points).map(({ y }) => y);
  };

  const pressureLines = lines.filter(({ look }) => look.onPressureAxis);
  const elevation = scaleOf(heightsOf([
    ...lines.filter(({ look }) => !look.onPressureAxis).flatMap(({ points }) => points),
    ...markers.flatMap(({ data }) => data),
  ]), 8, 'y', ELEVATION_PADDING);
  const pressure = scaleOf(heightsOf(pressureLines.flatMap(({ points }) => points)), 8, 'y');
  const hasPressureAxis = pressureLines.length > 0;

  const { text, subtext } = buildHglImageTitle(context);
  const legendRows = estimateLegendRows([...lines.map(({ look }) => look.label), ...markers.map(({ name }) => name)]);
  const legendHeight = legendRows * LEGEND_ROW_HEIGHT + (legendRows - 1) * LEGEND_ITEM_GAP;

  const xAxis: XAXisComponentOption = {
    ...valueAxis(`${i18n._('Distance from Pump ').trim()} (${distanceAbbr})`, distance, formatNumber),
    nameGap: X_AXIS_NAME_GAP,
  };
  const yAxis: YAXisComponentOption[] = [
    { ...valueAxis(`${i18n._('Elevation')} (${distanceAbbr})`, elevation, formatNumber), position: 'left', nameGap: 60 },
    ...(hasPressureAxis
      ? [{ ...valueAxis(`${i18n._('Pressure')} (${pressureAbbr})`, pressure, formatNumber), position: 'right' as const, nameGap: 60 }]
      : []),
  ];

  const lineSeries: LineSeriesOption[] = lines.map(({ look, points }) => ({
    type: 'line',
    name: look.label,
    yAxisIndex: look.onPressureAxis ? 1 : 0,
    data: points.map(({ x, y }) => [x, y]),
    symbol: 'none',
    silent: true,
    clip: true,
    lineStyle: { color: look.color, width: look.width, type: look.dashed ? 'dashed' : 'solid' },
    itemStyle: { color: look.color },
  }));

  const markerSeries: (LineSeriesOption | ScatterSeriesOption)[] = markers.map(({
    name, color, data, lines: positions,
  }) => {
    const markLine = positions.length > 0
      ? {
        symbol: 'none',
        silent: true,
        label: { show: false },
        lineStyle: { color, type: 'solid' as const, width: 1.5 },
        data: positions.map((x) => ({ xAxis: x })),
      }
      : undefined;
    return data.length > 0
      ? {
        type: 'scatter',
        name,
        data: data.map(({ x, y }) => [x, y]),
        symbolSize: 7,
        silent: true,
        clip: true,
        itemStyle: { color, borderColor: INK, borderWidth: 1 },
        markLine,
      }
      : {
        type: 'line',
        name,
        data: [],
        lineStyle: { color, width: 1.5 },
        itemStyle: { color },
        markLine,
      };
  });

  return {
    animation: false,
    backgroundColor: PAPER,
    textStyle: { fontFamily: FONT_FAMILY, color: INK },
    title: {
      text,
      subtext,
      left: 'center',
      top: 12,
      itemGap: 8,
      textStyle: { color: INK, fontSize: 16, fontWeight: 'normal' },
      subtextStyle: { color: INK, fontSize: 15 },
    },
    grid: {
      show: true,
      left: 90,
      right: hasPressureAxis ? 90 : 40,
      top: subtext ? 80 : 56,
      bottom: LEGEND_BOTTOM + legendHeight + LEGEND_GAP_BELOW_AXIS_NAME + X_AXIS_NAME_HEIGHT + X_AXIS_NAME_GAP,
      borderColor: INK,
      borderWidth: 1,
      backgroundColor: PAPER,
    },
    legend: {
      bottom: LEGEND_BOTTOM,
      left: 'center',
      selectedMode: false,
      itemWidth: LEGEND_ICON_WIDTH,
      itemHeight: 10,
      itemGap: LEGEND_ITEM_GAP,
      textStyle: { color: INK, fontSize: 13 },
      data: [
        ...lines.map(({ look }) => ({ name: look.label, itemStyle: LINE_ONLY_LEGEND_ICON })),
        ...markers.map(({ name, data }) => (data.length > 0 ? { name } : { name, itemStyle: LINE_ONLY_LEGEND_ICON })),
      ],
    },
    xAxis,
    yAxis,
    series: [...lineSeries, ...markerSeries],
  };
};

const downloadDataUrl = (url: string, filename: string) => {
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const renderHglImageDataUrl = async (context: HglImageContext, view?: DistanceWindow): Promise<string | undefined> => {
  if (!context.series.some(({ data }) => data.some(isPlotted))) return undefined;

  const [echarts, { LineChart, ScatterChart }, {
    GridComponent, LegendComponent, MarkLineComponent, TitleComponent,
  }, { CanvasRenderer }] = await Promise.all([
    import('echarts/core'),
    import('echarts/charts'),
    import('echarts/components'),
    import('echarts/renderers'),
  ]);
  echarts.use([LineChart, ScatterChart, GridComponent, LegendComponent, MarkLineComponent, TitleComponent, CanvasRenderer]);

  const host = document.createElement('div');
  Object.assign(host.style, {
    position: 'fixed', left: '-10000px', top: '0', width: `${WIDTH}px`, height: `${HEIGHT}px`,
  });
  document.body.appendChild(host);
  const chart = echarts.init(host, undefined, { renderer: 'canvas', width: WIDTH, height: HEIGHT });
  try {
    chart.setOption(buildHglImageOption(context, view));
    return chart.getDataURL({ type: 'png', pixelRatio: PIXEL_RATIO, backgroundColor: PAPER });
  } finally {
    chart.dispose();
    host.remove();
  }
};

export const exportHglImage = async (context: HglImageContext, view?: DistanceWindow): Promise<void> => {
  const dataUrl = await renderHglImageDataUrl(context, view);
  if (!dataUrl) return;
  downloadDataUrl(dataUrl, buildHglFilename('png', context.summary.stopeName));
};
